import React, { useRef } from "react";
import {
  BUTTON_VARIANTS,
  Content,
  CONTENT_TYPES,
  DialogFooterButtonProps,
  IconFileDownload,
  IconSave,
  TextButton,
  TOAST_VISIBILITY_DURATIONS,
} from "@cdkglobal/radial";
import { ColDef } from "ag-grid-community";

import {
  StyledChip,
  StyledFileInput,
  StyledFileUploadDialog,
  StyledTemplateUpload,
} from "../../../../../utils/styled";
import Toaster from "../../../../common/Toaster";
import ProgressBar from "../../../../common/ProgressBar";
import FileUploadTable from "./FileUploadTable";
import { UploadState } from "./constants";
import { RowData, ToastInfo } from "../../../../../types";

interface FileUploadDialogProps {
  isOpen: boolean;
  title: string;
  uploadState: UploadState;
  attachedFile: File | null;
  toastMessage: ToastInfo;
  columnDefs: ColDef[];
  tableData: RowData[];
  hasErrors: boolean;
  onClose: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFileRemove: () => void;
  onValidate: () => void;
  onImport: () => void;
  onReset: () => void;
  onDownloadTemplate: () => void;
  onDownloadErrorReport: () => void;
  onClearToast: () => void;
}

export default function FileUploadDialog({
  isOpen,
  title,
  uploadState,
  attachedFile,
  toastMessage,
  columnDefs,
  tableData,
  hasErrors,
  onClose,
  onFileChange,
  onFileRemove,
  onValidate,
  onImport,
  onReset,
  onDownloadTemplate,
  onDownloadErrorReport,
  onClearToast,
}: Readonly<FileUploadDialogProps>): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showTable =
    uploadState === UploadState.VALIDATION_COMPLETE ||
    uploadState === UploadState.IMPORT_COMPLETE;

  const loadingLabel =
    uploadState === UploadState.VALIDATING
      ? "Validating file..."
      : "Importing records...";

  const getPrimaryButton = (): DialogFooterButtonProps | null => {
    if (uploadState === UploadState.PARSING_ERROR) {
      return {
        id: "file-upload-reupload",
        text: "Reupload",
        variant: BUTTON_VARIANTS.PRIMARY,
        onClick: onReset,
      };
    }

    if (uploadState === UploadState.IDLE && attachedFile) {
      return {
        id: "file-upload-validate",
        dataTestId: "file-upload-validate",
        text: "Import",
        variant: BUTTON_VARIANTS.PRIMARY,
        onClick: onValidate,
      };
    }

    if (uploadState === UploadState.VALIDATION_COMPLETE) {
      const id = hasErrors ? "file-upload-reupload" : "file-upload-import";

      return {
        id,
        dataTestId: id,
        text: hasErrors ? "Reupload" : "Proceed",
        variant: BUTTON_VARIANTS.PRIMARY,
        onClick: hasErrors ? onReset : onImport,
      };
    }

    return null;
  };

  const primaryButton = getPrimaryButton();
  const cancelButton: DialogFooterButtonProps = {
    id: "file-upload-cancel",
    text: uploadState === UploadState.IMPORT_COMPLETE ? "Close" : "Cancel",
    variant: BUTTON_VARIANTS.TEXT,
    onClick: onClose,
    isDisabled:
      uploadState === UploadState.VALIDATING ||
      uploadState === UploadState.IMPORTING,
  };

  const buttons: DialogFooterButtonProps[] = primaryButton
    ? [cancelButton, primaryButton]
    : [cancelButton];

  const leftButton =
    uploadState === UploadState.VALIDATION_COMPLETE && hasErrors
      ? {
          id: "download-error-report",
          dataTestId: "download-error-report",
          text: "Download Error Report",
          variant: BUTTON_VARIANTS.TEXT,
          icon: <IconFileDownload />,
          isDisabled: tableData.length === 0,
          onClick: onDownloadErrorReport,
        }
      : undefined;

  return (
    <StyledFileUploadDialog
      id="file-upload-dialog"
      dataTestId="file-upload"
      isOpen={isOpen}
      onClose={onClose}
      buttonsProps={buttons}
      leftButtonProps={leftButton}
      title={title}
      style={{
        width: showTable ? "80vw" : "40vw",
      }}
    >
      {toastMessage.message.length > 0 && (
        <Toaster
          variant={toastMessage.variant}
          message={toastMessage.message}
          clearMessage={onClearToast}
          noActionButton
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.INFINITE}
        />
      )}

      {(uploadState === UploadState.VALIDATING ||
        uploadState === UploadState.IMPORTING) && (
        <ProgressBar
          label={loadingLabel}
          loaderSize="large"
          trackWidth="100%"
          style={{ margin: "1rem 0" }}
        />
      )}

      {uploadState === UploadState.IDLE && (
        <>
          <Content type={CONTENT_TYPES.BODY_2}>
            Import multiple records at once with File Upload. The required data
            format can be downloaded{" "}
            <a
              href=""
              onClick={(event) => {
                event.preventDefault();
                onDownloadTemplate();
              }}
            >
              here
            </a>
            .
            <br />
            Process can take several minutes. Please be patient.
          </Content>

          <br />

          <Content type={CONTENT_TYPES.BODY_2}>
            Supported file formats: .xlsx, .csv
          </Content>

          <br />

          <StyledTemplateUpload>
            <TextButton
              icon={<IconSave />}
              text="Select File"
              onClick={() => fileInputRef.current?.click()}
            />

            <StyledFileInput
              id="file-upload-file-attachments-input"
              data-testid="file-upload-file-attachments-input"
              type="file"
              accept=".xlsx,.csv"
              multiple={false}
              ref={fileInputRef}
              onChange={onFileChange}
            />

            {attachedFile && (
              <StyledChip label={attachedFile.name} onRemove={onFileRemove} />
            )}
          </StyledTemplateUpload>
        </>
      )}

      {showTable && (
        <FileUploadTable
          languageSpecificColDefs={columnDefs}
          dataToDisplay={tableData}
          fileUploadState={
            uploadState === UploadState.VALIDATION_COMPLETE
              ? "validationComplete"
              : "importComplete"
          }
        />
      )}
    </StyledFileUploadDialog>
  );
}
